import React, { useState } from "react";

import PartLoader from "../PartLoader";

type onClickT = (
  event?: React.MouseEvent<HTMLElement | HTMLButtonElement | HTMLLinkElement>,
) => Promise<void> | void;

type PartPendingActionPropChildAsFn = ({
  onClick,
}: {
  onClick: onClickT;
}) => React.ReactElement;

interface PartPendingActionWrapperProps {
  /**
   * Display block style on loader wrapper
   */
  block?: boolean;
  /**
   * Is loader active and should show
   */
  pending?: boolean;
  /**
   * Delay of showing loader after active prop is true (ms)
   */
  delay?: number;
  /**
   * Transition of toggling loader (ms)
   */
  transitionDuration?: number;
  /**
   * Click handler passed to root component in slot
   */
  onClick?: onClickT;
  /**
   * Children to be wrapped
   */
  children: PartPendingActionPropChildAsFn | React.ReactElement;
}

const PartPendingActionWrapper: React.FC<PartPendingActionWrapperProps> = ({
  block = false,
  pending = false,
  delay = 0,
  transitionDuration = 300,
  onClick,
  children,
}) => {
  const [localPending, setLocalPending] = useState(false);

  const handleClick: onClickT = React.useCallback(
    async (...args) => {
      if (!onClick) return;

      try {
        setLocalPending(true);
        // await children.
        await onClick(...args);
      } finally {
        setLocalPending(false);
      }
    },
    [onClick],
  );

  const isPending = pending || localPending;

  if (typeof children === "function") {
    // If children is a function, call it with the onClick handler
    return (
      <PartLoader
        active={isPending}
        block={block}
        delay={delay}
        transitionDuration={transitionDuration}
      >
        {children({ onClick: handleClick })}
      </PartLoader>
    );
  }

  // If children is a React element, clone it with the onClick handler
  // Ensure children is a valid React element before cloning
  if (React.isValidElement(children)) {
    // Cast children to a known type to avoid the error
    const child = children as React.ReactElement<{ onClick?: onClickT }>;
    return (
      <PartLoader
        active={isPending}
        block={block}
        delay={delay}
        transitionDuration={transitionDuration}
      >
        {React.cloneElement(child, { onClick: handleClick })}
      </PartLoader>
    );
  }

  // Handle the case where children is not a valid React element
  return (
    <PartLoader
      active={isPending}
      block={block}
      delay={delay}
      transitionDuration={transitionDuration}
    >
      {children}
    </PartLoader>
  );
};

export default PartPendingActionWrapper;
